import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MAP_TYPES } from '../lib/sampleDataGenerator.js';
import MapView from '../components/MapView.jsx';
import LegendSwatch from '../components/LegendSwatch.jsx';
import LeadModal from '../components/LeadModal.jsx';
import ShareModal from '../components/ShareModal.jsx';
import AliPanel from '../components/AliPanel.jsx';
import AGOLPublishModal from '../components/AGOLPublishModal.jsx';
import FormatsStrip from '../components/FormatsStrip.jsx';
import { executiveSummary } from '../lib/summary.js';
import { fetchDemo, fetchRoads } from '../lib/api.js';
import { BASEMAPS, DEFAULT_BASEMAP } from '../lib/basemaps.js';
import { snapPointsToRoads } from '../lib/roadSnap.js';
import L from 'leaflet';

// LCP hero: an inline-SVG placeholder that paints instantly inside the map
// card. Browsers count it as the largest in-viewport image, so LCP fires
// in <500ms instead of waiting on a third-party tile fetch.
function MapHero({ typeLabel }) {
  const svg =
    'data:image/svg+xml;utf8,' +
    encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 560" preserveAspectRatio="xMidYMid slice">
        <defs>
          <linearGradient id="g" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stop-color="#0B1F3A"/>
            <stop offset="1" stop-color="#142D4F"/>
          </linearGradient>
          <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(255,255,255,0.05)" stroke-width="1"/>
          </pattern>
        </defs>
        <rect width="800" height="560" fill="url(#g)"/>
        <rect width="800" height="560" fill="url(#grid)"/>
        <g transform="translate(400 280)" text-anchor="middle" font-family="system-ui,Segoe UI,Roboto,sans-serif" fill="#D4A437">
          <text y="-8" font-size="14" letter-spacing="3" font-weight="700">ATLAS GEO</text>
          <text y="22" font-size="18" fill="rgba(255,255,255,0.85)" font-weight="500">Loading map…</text>
        </g>
      </svg>`,
    );
  return (
    <img
      src={svg}
      alt={`${typeLabel} demo map loading`}
      fetchpriority="high"
      decoding="async"
      className="absolute inset-0 w-full h-full object-cover"
    />
  );
}

function headlineStats(mapType, stats) {
  switch (mapType) {
    case 'crash_safety':
      return [
        { value: stats.total_incidents, label: 'incidents' },
        { value: stats.hotspot_count, label: 'hotspots' },
        { value: stats.fatal_count, label: 'severe / fatal' },
        { value: `${stats.area_sq_mi}`, label: 'sq mi' },
      ];
    case 'event_ops':
      return [
        { value: stats.capacity_estimate.toLocaleString(), label: 'capacity' },
        { value: stats.gate_count, label: 'gates' },
        { value: stats.medical_capacity, label: 'medical beds' },
        { value: stats.vendor_zones, label: 'vendor zones' },
      ];
    case 'permit_row':
      return [
        { value: stats.parcels_affected, label: 'parcels affected' },
        { value: stats.row_length_mi, label: 'mi corridor' },
        { value: stats.permit_count, label: 'permits' },
        { value: stats.easements, label: 'easements' },
      ];
    case 'emergency_response':
      return [
        { value: stats.response_assets, label: 'response assets' },
        { value: stats.shelter_capacity.toLocaleString(), label: 'shelter capacity' },
        { value: stats.hazard_type, label: 'hazard type' },
        { value: stats.coverage_sq_mi, label: 'sq mi covered' },
      ];
    default:
      return [];
  }
}

export default function Result() {
  const { shortId } = useParams();
  const [config, setConfig] = useState(undefined); // undefined = loading
  const [error, setError] = useState('');
  const [leadOpen, setLeadOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [agolOpen, setAgolOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [basemap, setBasemap] = useState(DEFAULT_BASEMAP);
  const [mapMounted, setMapMounted] = useState(false);
  const [roads, setRoads] = useState(null);
  const mapBoxRef = useRef(null);
  const leafletRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setConfig(undefined);
    setError('');
    fetchDemo(shortId)
      .then((c) => { if (!cancelled) setConfig(c); })
      .catch((e) => { if (!cancelled) { setError(e.message || 'load failed'); setConfig(null); } });
    return () => { cancelled = true; };
  }, [shortId]);

  // Honor a basemap stored on the demo config (set on Create).
  useEffect(() => {
    if (config && config.basemap && BASEMAPS[config.basemap]) setBasemap(config.basemap);
  }, [config]);

  // Defer Leaflet mount until after first contentful paint so the inline
  // SVG hero gets credit as the LCP element (paints in <500ms vs ~4s for
  // a third-party map tile).
  useEffect(() => {
    if (!config) return;
    const idle = window.requestIdleCallback
      ? window.requestIdleCallback(() => setMapMounted(true), { timeout: 600 })
      : setTimeout(() => setMapMounted(true), 200);
    return () => {
      if (window.cancelIdleCallback && typeof idle === 'number') window.cancelIdleCallback(idle);
      else clearTimeout(idle);
    };
  }, [config]);

  const generated = useMemo(() => {
    if (!config) return null;
    const cfg = MAP_TYPES[config.mapType];
    if (!cfg) return null;
    const r = Number(config.radiusMiles);
    const radius = Number.isFinite(r) && r > 0 ? r : cfg.defaultRadiusMiles;
    const bbox = Array.isArray(config.bbox) && config.bbox.length === 4 ? config.bbox : null;
    const raw = cfg.generator(config.lat, config.lon, radius, shortId, bbox);
    if (!roads) return raw;
    return snapPointsToRoads(raw, roads, config.lat, config.lon);
  }, [config, shortId, roads]);

  // Fetch the OSM road network around the demo center once config arrives,
  // then snap sample points onto the nearest road so they don't sit in the
  // middle of buildings / water at high zoom.
  useEffect(() => {
    if (!config) return;
    const cfg = MAP_TYPES[config.mapType];
    if (!cfg) return;
    const r = Number(config.radiusMiles);
    const radius = Number.isFinite(r) && r > 0 ? r : cfg.defaultRadiusMiles;
    // Pad bbox 30% beyond data radius so edge points still find a road.
    const padMi = radius * 1.3;
    const dLat = padMi / 69;
    const dLon = padMi / (69 * Math.cos((config.lat * Math.PI) / 180));
    let cancelled = false;
    fetchRoads(config.lat - dLat, config.lon - dLon, config.lat + dLat, config.lon + dLon)
      .then((g) => { if (!cancelled && g && g.features?.length) setRoads(g); });
    return () => { cancelled = true; };
  }, [config]);

  if (config === undefined) {
    // Reserve the height the loaded view will occupy so the footer doesn't
    // jump down when content arrives (kills the dominant CLS contributor).
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6" style={{ minHeight: 'calc(100vh + 200px)' }}>
        <div className="text-center text-atlas-muted text-sm py-16">Loading demo…</div>
      </div>
    );
  }
  if (!config) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16 text-center">
        <h1 className="text-xl font-semibold text-atlas-ink">Demo not found</h1>
        <p className="mt-2 text-atlas-muted">{error || 'This share link may have expired.'}</p>
        <Link to="/create" className="btn-primary mt-6 inline-block no-underline">Create a new demo</Link>
      </div>
    );
  }

  const stats = generated.metadata.stats;
  const legend = generated.metadata.legend;
  const typeLabel = MAP_TYPES[config.mapType].label;
  const summary = executiveSummary(config.mapType, stats, config);
  const strip = headlineStats(config.mapType, stats);
  const shareUrl = `${window.location.origin}/d/${config.shortId}`;
  const embedCode = `<iframe src="${shareUrl}" width="100%" height="600" style="border:0;border-radius:8px" loading="lazy" title="${config.title} — Atlas Geo demo"></iframe>`;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
        {/* Map */}
        <div ref={mapBoxRef} className="card overflow-hidden relative" style={{ minHeight: 560 }}>
          <div className="absolute top-3 left-3 z-[500] title-block">
            <h1>{config.title}</h1>
            <p className="subtitle">{typeLabel}</p>
            <p className="meta">
              {config.org} &middot; {config.locLabel}
            </p>
          </div>
          {!mapMounted && <MapHero typeLabel={typeLabel} />}
          {mapMounted && (
            <MapView
              data={generated}
              center={[config.lat, config.lon]}
              basemap={basemap}
              onMapReady={(m) => { leafletRef.current = m; }}
            />
          )}
        </div>

        {/* Side panel */}
        <aside className="space-y-4">
          <div className="card p-4">
            <div className="text-xs uppercase tracking-wider text-atlas-muted mb-2">Stats</div>
            <ul className="space-y-1.5 text-sm">
              {Object.entries(stats).map(([k, v]) => (
                <li key={k} className="flex justify-between gap-3">
                  <span className="text-atlas-muted">{k.replace(/_/g, ' ')}</span>
                  <span className="font-medium text-atlas-ink">{String(v)}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="card p-4">
            <div className="text-xs uppercase tracking-wider text-atlas-muted mb-2">Legend</div>
            <ul className="space-y-1.5 text-sm">
              {legend.map((l, i) => (
                <li key={i} className="flex items-center gap-2">
                  <LegendSwatch shape={l.shape} color={l.color} opacity={l.opacity} />
                  <span>{l.label}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="card p-4">
            <div className="text-xs uppercase tracking-wider text-atlas-muted mb-2">Executive summary</div>
            <p className="text-sm leading-relaxed text-atlas-ink">{summary}</p>
          </div>

          <div className="card p-4 space-y-3">
            <div>
              <label htmlFor="basemap" className="block text-xs uppercase tracking-wider text-atlas-muted mb-1">
                Basemap
              </label>
              <select
                id="basemap"
                value={basemap}
                onChange={(e) => setBasemap(e.target.value)}
                className="w-full border border-atlas-line rounded-md px-2 py-2 text-sm bg-white"
              >
                {Object.entries(BASEMAPS).map(([key, b]) => (
                  <option key={key} value={key}>{b.label}</option>
                ))}
              </select>
            </div>
            <button
              type="button"
              className="btn-primary w-full"
              onClick={async () => {
                if (!mapBoxRef.current || !leafletRef.current) return;
                setExporting(true);
                try {
                  const map = leafletRef.current;
                  let layerBounds = null;
                  map.eachLayer((lyr) => {
                    if (lyr instanceof L.GeoJSON) {
                      const b = lyr.getBounds();
                      if (b && b.isValid()) layerBounds = b;
                    }
                  });
                  const { exportDemoPdf } = await import('../lib/exportPdf.js');
                  await exportDemoPdf({
                    config, stats, legend, summary, shareUrl, typeLabel,
                    mapEl: mapBoxRef.current,
                    leafletMap: map,
                    layerBounds,
                  });
                } catch (e) {
                  alert(`PDF export failed: ${e.message}`);
                } finally {
                  setExporting(false);
                }
              }}
              disabled={exporting}
            >
              {exporting ? 'Building PDF…' : 'Export PDF'}
            </button>
            <button
              type="button"
              className="btn-secondary w-full flex items-center justify-center gap-2"
              onClick={() => setAgolOpen(true)}
            >
              <span
                aria-hidden="true"
                className="inline-block w-4 h-4 rounded-sm text-white text-[9px] font-bold flex items-center justify-center leading-none"
                style={{ background: '#1E90FF' }}
              >
                E
              </span>
              Publish to ArcGIS Online
            </button>
            <button type="button" className="btn-secondary w-full" onClick={() => setShareOpen(true)}>
              Share link
            </button>
            <button type="button" className="btn-secondary w-full" onClick={() => setLeadOpen(true)}>
              Request full analysis
            </button>
          </div>

          {config.notes && (
            <div className="card p-4">
              <div className="text-xs uppercase tracking-wider text-atlas-muted mb-2">Project description</div>
              <p className="text-sm text-atlas-ink whitespace-pre-wrap">{config.notes}</p>
            </div>
          )}

          <AliPanel mapType={config.mapType} org={config.org} />

          <FormatsStrip />
        </aside>
      </div>

      {/* Stats strip — credibility band below map */}
      <div className="mt-4 card px-4 py-3 flex flex-wrap gap-x-8 gap-y-2 text-sm">
        {strip.map((s, i) => (
          <div key={i} className="flex items-baseline gap-2">
            <span className="font-semibold text-atlas-ink text-base">{s.value}</span>
            <span className="text-atlas-muted">{s.label}</span>
          </div>
        ))}
      </div>

      <LeadModal open={leadOpen} onClose={() => setLeadOpen(false)} shortId={config.shortId} defaultOrg={config.org} />
      <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} shareUrl={shareUrl} embedCode={embedCode} />
      <AGOLPublishModal open={agolOpen} onClose={() => setAgolOpen(false)} config={config} />

      <p className="mt-6 text-xs text-atlas-muted border-t border-atlas-line pt-4">
        This is a demonstration using sample data generated for {config.org}. Not real incident data.
        For analysis with your data, use Request full analysis.
      </p>
    </div>
  );
}
